import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption<T extends string = string> {
  label: string;
  value: T;
}

interface DropdownProps<T extends string = string> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

export function Dropdown<T extends string = string>({
  options,
  value,
  onChange,
  className = '',
}: DropdownProps<T>) {
  const selected = options.find((o) => o.value === value);

  return (
    <Menu as="div" className={`relative inline-block ${className}`}>
      <MenuButton className="inline-flex items-center rounded-lg! w-44 gap-1.5 p-3 text-sm font-medium text-(--neutral-black-NB900) bg-(--neutral-white-NW1000) hover:bg-gray-100 focus:outline-none cursor-pointer whitespace-nowrap h-full justify-between">
        {selected?.label}
        <ChevronDown size={14} className="text-(--neutral-black-NB600) shrink-0" />
      </MenuButton>

      <MenuItems
        transition
        className="absolute left-0 z-10 w-44 origin-top-left rounded-md bg-(--neutral-white-NW1000) shadow-lg outline-1 outline-black/5 transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
      >
        {options.map((opt) => (
          <MenuItem key={opt.value}>
            <button
              type="button"
              onClick={() => onChange(opt.value)}
              className={`block w-full px-4 py-3 text-left text-sm cursor-pointer
                                    data-focus:bg-gray-100 data-focus:outline-none
                                    ${
                                      value === opt.value
                                        ? 'text-(--primary-O900) font-semibold'
                                        : 'text-(--neutral-black-NB800)'
                                    }`}
            >
              {opt.label}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
